import { InputWithLabel, SelectWithLabel } from '@adotel/components'
import { useMutation, useQuery } from '@apollo/client'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import React, { useState } from 'react'
import { toast } from 'react-toastify'

import {
  RESOLVE_TICKET_SLA_POLICY_QUERY,
  TICKET_SLA_POLICIES_QUERY,
  UPSERT_TICKET_SLA_POLICY_MUTATION
} from '../../graphql/tickets'
import {
  draftFromPolicy,
  DraftErrors,
  DURATION_UNITS,
  DurationUnit,
  hasErrors,
  SlaPolicy,
  SlaPolicyDraft,
  toSeconds,
  validateDraft,
  WILDCARD
} from '../../helpers/slaPolicies'
import { getTranslate } from '../../localization'

// The four levels the priority enum carries, plus the wildcard. Free text
// would let a policy be written against a priority no ticket can have — it
// would save, and then govern nothing, with no error anywhere.
const PRIORITIES = ['P1', 'P2', 'P3', 'P4', WILDCARD]
const ENTITY_TYPES = ['accommodation', 'channel', 'provider', WILDCARD]

interface Props {
  /** Undefined creates a new policy; a policy edits that version chain. */
  editing?: SlaPolicy
  includeRetired: boolean
  onClose: () => void
}

export default function SlaPolicyForm({
  editing,
  includeRetired,
  onClose
}: Props) {
  const [draft, setDraft] = useState<SlaPolicyDraft>(() =>
    draftFromPolicy(editing)
  )
  const [errors, setErrors] = useState<DraftErrors>({})

  const [upsert, upsertState] = useMutation<{
    upsertTicketSlaPolicy: SlaPolicy
  }>(UPSERT_TICKET_SLA_POLICY_MUTATION, {
    refetchQueries: [
      { query: TICKET_SLA_POLICIES_QUERY, variables: { includeRetired } }
    ]
  })

  // Shows which policy governs this selector TODAY, resolved by the server
  // using the create path's own precedence.
  //
  // This is the whole reason the form is safe to use: a customer override
  // outranks a platform policy even a narrower one, and getting that wrong is
  // invisible — the admin sees a saved policy that never applies. Asking the
  // server what currently wins is the only honest way to show it, and
  // re-deriving the ordering here would be a second copy of the one rule that
  // fails silently.
  const canPreview = Boolean(
    draft.reporterEntityCode.trim() && draft.priority.trim()
  )
  const { data: currentData } = useQuery<{
    resolveTicketSlaPolicy: SlaPolicy
  }>(RESOLVE_TICKET_SLA_POLICY_QUERY, {
    variables: {
      reporterEntityCode: draft.reporterEntityCode.trim(),
      reporterEntityType: draft.reporterEntityType.trim(),
      priority: draft.priority.trim()
    },
    skip: !canPreview
  })
  const currentlyGoverning = currentData?.resolveTicketSlaPolicy

  function set<K extends keyof SlaPolicyDraft>(
    key: K,
    value: SlaPolicyDraft[K]
  ) {
    setDraft((previous) => ({ ...previous, [key]: value }))
    // Clear this field's error as it is corrected. Errors were recomputed only
    // inside submit, so red helper text survived the fix that resolved it
    // until the operator pressed Save again and watched it disappear.
    // DraftErrors keys are a SUBSET of the draft's, so the clear is narrowed
    // rather than cast — a draft field with no error entry is not an error.
    setErrors((previous) => {
      if (!(key in previous)) return previous
      const next = { ...previous }
      delete next[key as keyof DraftErrors]

      return next
    })
  }

  function submit(event?: React.FormEvent) {
    event?.preventDefault()
    const found = validateDraft(draft)
    setErrors(found)
    if (hasErrors(found)) return

    upsert({
      variables: {
        policyId: draft.policyId.trim(),
        reporterEntityCode: draft.reporterEntityCode.trim(),
        reporterEntityType: draft.reporterEntityType.trim(),
        priority: draft.priority.trim(),
        respondAfterSeconds: toSeconds(
          Number(draft.respondValue),
          draft.respondUnit
        ),
        resolveAfterSeconds: toSeconds(
          Number(draft.resolveValue),
          draft.resolveUnit
        ),
        // The version being replaced, or 0 asserting this policy has no active
        // version yet. Sending the wrong one either discards a concurrent edit
        // or fails — the server decides, this only reports what it saw.
        expectedVersion: editing ? editing.version : 0
      }
    })
      .then(() => {
        // Creating a ticket toasts; changing a customer's contractual response
        // targets did not. The higher-consequence action had the weaker
        // feedback -- the row reappearing with a new version number was the
        // only signal, and only if the admin knew to look for it.
        toast.success(getTranslate('Policy version saved.'))
        onClose()
      })
      .catch((e) => toast.error(e.message))
  }

  function durationRow(
    label: string,
    valueKey: 'respondValue' | 'resolveValue',
    unitKey: 'respondUnit' | 'resolveUnit',
    error?: string
  ) {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <InputWithLabel
          label={label}
          type="number"
          fullWidth
          inputSize="small"
          value={draft[valueKey]}
          error={Boolean(error)}
          helpertext={error ? getTranslate(error) : undefined}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            set(valueKey, event.target.value)
          }
        />
        <SelectWithLabel
          select
          label={getTranslate('Unit')}
          value={draft[unitKey]}
          style={{ minWidth: 110 }}
          onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
            set(unitKey, event.target.value as DurationUnit)
          }>
          {DURATION_UNITS.map((unit) => (
            <MenuItem key={unit} value={unit}>
              {getTranslate(unit)}
            </MenuItem>
          ))}
        </SelectWithLabel>
      </div>
    )
  }

  return (
    // aria-labelledby is wired by hand: MUI v4 does not derive the dialog's
    // accessible name from DialogTitle, so without it a screen reader
    // announces an unlabelled dialog.
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="sla-policy-dialog-title">
      <DialogTitle id="sla-policy-dialog-title">
        {editing
          ? `${getTranslate('Edit')} ${editing.policyId}`
          : getTranslate('New SLA policy')}
      </DialogTitle>
      {/* A real form element, so Enter submits from any field. Without it the
          only way to save is reaching the button, which on a dialog this
          shape is a mouse trip for every edit. */}
      <form onSubmit={submit}>
        <DialogContent>
          <InputWithLabel
            label={getTranslate('Policy id')}
            fullWidth
            inputSize="small"
            // Fixed while editing: the id is the identity a version chain hangs
            // off, so changing it creates a second policy rather than editing
            // this one.
            disabled={Boolean(editing)}
            value={draft.policyId}
            error={Boolean(errors.policyId)}
            helpertext={
              errors.policyId ? getTranslate(errors.policyId) : undefined
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              set('policyId', event.target.value)
            }
          />

          <InputWithLabel
            label={getTranslate('Entity code')}
            fullWidth
            inputSize="small"
            value={draft.reporterEntityCode}
            error={Boolean(errors.reporterEntityCode)}
            helpertext={
              errors.reporterEntityCode
                ? getTranslate(errors.reporterEntityCode)
                : getTranslate("'*' for a platform-wide policy")
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              set('reporterEntityCode', event.target.value)
            }
          />

          <SelectWithLabel
            select
            fullWidth
            label={getTranslate('Entity type')}
            value={draft.reporterEntityType}
            error={Boolean(errors.reporterEntityType)}
            helpertext={
              errors.reporterEntityType
                ? getTranslate(errors.reporterEntityType)
                : undefined
            }
            onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
              set('reporterEntityType', event.target.value as string)
            }>
            {ENTITY_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </SelectWithLabel>

          <SelectWithLabel
            select
            fullWidth
            label={getTranslate('Priority')}
            value={draft.priority}
            error={Boolean(errors.priority)}
            helpertext={
              errors.priority ? getTranslate(errors.priority) : undefined
            }
            onChange={(event: React.ChangeEvent<{ value: unknown }>) =>
              set('priority', event.target.value as string)
            }>
            {PRIORITIES.map((priority) => (
              <MenuItem key={priority} value={priority}>
                {priority}
              </MenuItem>
            ))}
          </SelectWithLabel>

          {durationRow(
            getTranslate('Respond within'),
            'respondValue',
            'respondUnit',
            errors.respondValue
          )}
          {durationRow(
            getTranslate('Resolve within'),
            'resolveValue',
            'resolveUnit',
            errors.resolveValue
          )}

          {/* Product scope and intake channel are absent on purpose: they stay
            platform-controlled, and the server copies them forward from the
            version being replaced. */}
          <Typography variant="caption" color="textSecondary">
            {getTranslate(
              'Targets are elapsed time on a 24/7 clock. Product scope and intake channel are platform-controlled and carried over unchanged.'
            )}
          </Typography>

          {/* role="status" because this appears and CHANGES as the selector is
            typed. It is the only thing on screen telling an admin whether the
            policy they are writing will actually take effect, so a
            screen-reader user must not be the one person who cannot see it. */}
          {currentlyGoverning && (
            <Alert severity="info" role="status" style={{ marginTop: 12 }}>
              {getTranslate('Currently governed by')}{' '}
              <bdi dir="ltr">
                {currentlyGoverning.policyId} v{currentlyGoverning.version}
              </bdi>
              {currentlyGoverning.policyId === draft.policyId.trim()
                ? ` — ${getTranslate('this policy')}`
                : ` — ${getTranslate('saving will override it')}`}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {/* No explicit type needed: MUI's ButtonBase already defaults to
              "button", so Cancel does not submit the surrounding form. That is
              a plain-<button> hazard, not a MUI one — checked rather than
              assumed, because the two behave differently here. */}
          <Button onClick={onClose} disabled={upsertState.loading}>
            {getTranslate('Cancel')}
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            disabled={upsertState.loading}>
            {editing
              ? getTranslate('Save new version')
              : getTranslate('Create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
